import React from "react";
import { CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, X } from "lucide-react";
import {
  useGetDoctorsQuery,
  useGetGroupsQuery,
} from "@/features/members-groups/infrastructure/api/membersGroupsApi";
import { useTranslation } from "react-i18next";

interface RecommendedDoctorsSectionProps {
  patientId: string;
  assessmentId?: string;
  selectedDoctors: string[];
  onDoctorsChange: (doctorIds: string[]) => void;
}

export const RecommendedDoctorsSection: React.FC<
  RecommendedDoctorsSectionProps
> = ({ patientId, selectedDoctors, onDoctorsChange }) => {
  const { t } = useTranslation("patients");
  const { data: groups = [], isLoading: isGroupsLoading } = useGetGroupsQuery(
    undefined,
    { skip: !patientId },
  );
  const groupIds = groups.map((group) => group.id);
  const { data: doctors = [], isLoading: isDoctorsLoading } = useGetDoctorsQuery(
    { group_ids: groupIds },
    { skip: groupIds.length === 0 },
  );
  const isLoading = isGroupsLoading || isDoctorsLoading;

  const selectedDoctorList = selectedDoctors
    .map((id) => doctors.find((doctor) => doctor.id === id))
    .filter((doctor) => doctor !== undefined);
  const availableDoctors = doctors.filter(
    (doctor) => !selectedDoctors.includes(doctor.id),
  );

  const addDoctor = (doctorId: string) => {
    if (!doctorId || selectedDoctors.includes(doctorId)) return;
    onDoctorsChange([...selectedDoctors, doctorId]);
  };

  const removeDoctor = (doctorId: string) => {
    onDoctorsChange(selectedDoctors.filter((id) => id !== doctorId));
  };

  return (
    <div className="border-gray-200">
      <div className="pb-3">
        <CardTitle className="text-base font-semibold text-gray-900">
          {t("clinicalSections.recommendedDoctors.title")}
        </CardTitle>
        <p className="text-sm text-gray-500">
          {t("clinicalSections.recommendedDoctors.subtitle")}
        </p>
      </div>

      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("clinicalSections.recommendedDoctors.loadingDoctors")}
          </div>
        ) : groupIds.length === 0 || doctors.length === 0 ? (
          <p className="text-sm text-gray-400 py-4">
            {t("clinicalSections.recommendedDoctors.noDoctors")}
          </p>
        ) : (
          <div className="space-y-3">
            <Select
              value=""
              onValueChange={addDoctor}
              disabled={availableDoctors.length === 0}
            >
              <SelectTrigger className="h-12 w-full cursor-pointer">
                <SelectValue placeholder={t("clinicalSections.recommendedDoctors.selectDoctor")} />
              </SelectTrigger>

              <SelectContent
                position="popper"
                side="bottom"
                align="start"
                sideOffset={4}
                className="z-50"
              >
                {availableDoctors.map((doctor) => (
                  <SelectItem key={doctor.id} value={doctor.id}>
                    {doctor.name}
                    {doctor.role ? ` (${doctor.role})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedDoctorList.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedDoctorList.map((doctor) => (
                  <div
                    key={doctor.id}
                    className="flex max-w-full items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-sm break-all"
                  >
                    {doctor.name}
                    {doctor.role ? ` (${doctor.role})` : ""}
                    <button
                      type="button"
                      className="cursor-pointer text-gray-500 hover:text-gray-900"
                      aria-label={t("clinicalSections.recommendedDoctors.removeDoctor", {
                        name: doctor.name,
                      })}
                      onClick={() => removeDoctor(doctor.id)}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
